import { fmt, minToHm } from "@/lib/units";
import { CheckCircle2, AlertTriangle } from "lucide-react";

const r1 = (v) => Math.round(v * 10) / 10;
const inputCls = "w-20 rounded-md border border-slate-700 bg-slate-900/60 px-2 py-1 font-mono-data text-sm text-slate-100 focus:border-cyan-500 focus:outline-none";
const Box = ({ children, grey }) => <span className={`font-mono-data text-sm ${grey ? "text-slate-500" : "text-slate-200"}`}>{children}</span>;

function FreeCells({ row, setFuel, ro }) {
  const { entry } = row;
  const empty = entry.v === "" || entry.v === undefined;
  const minVal = empty ? "" : entry.unit === "min" ? entry.v : r1(row.min);
  const lVal = empty ? "" : entry.unit === "L" ? entry.v : r1(row.L);
  const put = (unit) => (e) => setFuel(row.key, { v: e.target.value === "" ? "" : Number(e.target.value), unit });
  if (ro) return <><td className="px-3 py-2"><Box>{empty ? "—" : `${fmt(row.min)} min`}</Box></td><td className="px-3 py-2"><Box>{empty ? "—" : `${fmt(row.L, 1)} L`}</Box></td></>;
  return (
    <>
      <td className="px-3 py-2"><input type="number" value={minVal} onChange={put("min")} className={inputCls} data-testid={`fuel-${row.key}-min`} /></td>
      <td className="px-3 py-2"><input type="number" step="0.1" value={lVal} onChange={put("L")} className={inputCls} data-testid={`fuel-${row.key}-l`} /></td>
    </>
  );
}

function StdCell({ row, setFuel, ro }) {
  if (row.kind === "count") {
    if (ro) return <Box>{fmt(row.std)} min</Box>;
    return <input type="number" value={row.std} onChange={(e) => setFuel(`${row.key}Std`, e.target.value === "" ? "" : Number(e.target.value))} className={inputCls} data-testid={`fuel-${row.key}-std`} />;
  }
  if (row.kind === "reserve") {
    if (ro) return <Box>{row.night ? "Nuit" : "Jour"} · {row.std} min</Box>;
    return (
      <select value={row.night ? "night" : "day"} onChange={(e) => setFuel("night", e.target.value === "night")} data-testid="fuel-reserve-daynight"
        className="rounded-md border border-slate-700 bg-slate-900/60 px-2 py-1 text-sm text-slate-100 focus:outline-none">
        <option value="day">Jour · 30 min</option>
        <option value="night">Nuit · 45 min</option>
      </select>
    );
  }
  return <Box grey>—</Box>;
}

// Tableau des paramètres carburant (éditable si setFuel fourni).
export function FuelParamsTable({ plan, setFuel }) {
  const ro = !setFuel;
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[560px] border-collapse text-left text-sm" data-testid="fuel-params-table">
        <thead>
          <tr className="border-b border-slate-700 text-[10px] uppercase tracking-wider text-slate-400">
            <th className="px-3 py-2">Désignation</th><th className="px-3 py-2">Nombre</th><th className="px-3 py-2">Temps standard</th>
            <th className="px-3 py-2">Temps (min)</th><th className="px-3 py-2">Litrage (L)</th>
          </tr>
        </thead>
        <tbody>
          {plan.rows.map((row) => {
            const grey = row.kind === "fixed";
            return (
              <tr key={row.key} data-testid={`fuel-row-${row.key}`} className={`border-b border-slate-800 ${grey ? "bg-slate-800/30 text-slate-500" : ""}`}>
                <td className={`px-3 py-2 ${grey ? "text-slate-500" : "text-slate-300"}`}>{row.label}</td>
                <td className="px-3 py-2">
                  {row.kind === "count" ? (ro ? <Box>{row.count}</Box> :
                    <input type="number" min="0" value={row.count} onChange={(e) => setFuel(`${row.key}Count`, e.target.value === "" ? "" : Number(e.target.value))} className={inputCls} data-testid={`fuel-${row.key}-count`} />)
                    : <Box grey>—</Box>}
                </td>
                <td className="px-3 py-2"><StdCell row={row} setFuel={setFuel} ro={ro} /></td>
                {row.kind === "free" ? <FreeCells row={row} setFuel={setFuel} ro={ro} /> : (
                  <>
                    <td className="px-3 py-2"><Box grey={grey}><span data-testid={`fuel-${row.key}-min-value`}>{fmt(row.min)}</span> min</Box></td>
                    <td className="px-3 py-2"><Box grey={grey}><span data-testid={`fuel-${row.key}-l-value`}>{fmt(row.L, 1)}</span> L</Box></td>
                  </>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

const Row = ({ label, value, burn, accent, grey, tid }) => (
  <div className={`flex items-center justify-between border-b border-slate-800 py-2 last:border-0 ${grey ? "opacity-60" : ""}`}>
    <span className="text-sm text-slate-400">{label}</span>
    <span className="flex items-baseline gap-2">
      <span className={`font-mono-data text-sm font-semibold ${accent || "text-slate-100"}`} data-testid={tid}>{fmt(value, 1)} L</span>
      <span className="font-mono-data text-xs text-slate-500">· {minToHm(burn > 0 ? (value / burn) * 60 : 0)}</span>
    </span>
  </div>
);

export function FuelBilan({ plan, burn, p = "" }) {
  const ok = plan.margin >= 0;
  return (
    <div>
      <Row label="Forfait roulage" value={plan.taxi} burn={burn} />
      <Row label="Voyage (log de nav)" value={plan.trip} burn={burn} accent="text-cyan-300" tid={`${p}bilan-trip`} />
      <Row label="Marge aléas" value={plan.contingency} burn={burn} />
      <Row label="Forfait procédure" value={plan.procedure} burn={burn} />
      <Row label="Alternative déroutement" value={plan.alternate} burn={burn} />
      <Row label="Réserve réglementaire VFR" value={plan.reserve} burn={burn} />
      <Row label="Carburant non utilisable" value={plan.unusable} burn={burn} grey />
      <div className="my-2 border-t border-slate-700" />
      <Row label="Minimum réglementaire" value={plan.minRequired} burn={burn} accent="text-amber-300" tid={`${p}bilan-min`} />
      <Row label="Réserve exploitant CDB" value={plan.cdb} burn={burn} />
      <Row label="Total requis" value={plan.totalRequired} burn={burn} accent="text-amber-200" tid={`${p}bilan-total`} />
      <Row label="Carburant embarqué" value={plan.onboard} burn={burn} accent="text-cyan-300" />
      <div data-testid={`${p}fuel-margin-badge`}
        className={`mt-3 flex items-center justify-between gap-3 rounded-xl border p-3 ${ok ? "border-emerald-500/30 bg-emerald-500/10" : "border-rose-500/30 bg-rose-500/10"}`}>
        <div className="flex items-center gap-2">
          {ok ? <CheckCircle2 className="h-5 w-5 text-emerald-400" /> : <AlertTriangle className="h-5 w-5 text-rose-400" />}
          <span className={`text-sm font-semibold ${ok ? "text-emerald-300" : "text-rose-300"}`}>{ok ? "Marge suffisante" : "Carburant insuffisant"}</span>
        </div>
        <span className={`font-mono-data text-sm font-bold ${ok ? "text-emerald-300" : "text-rose-300"}`}>
          {ok ? "+" : ""}{fmt(plan.margin, 1)} L · {plan.marginTime >= 0 ? "+" : "−"}{minToHm(Math.abs(plan.marginTime))}
        </span>
      </div>
      <div className="mt-3 text-center text-xs text-slate-500">Autonomie embarquée : <span className="font-mono-data text-slate-300">{minToHm(plan.endurance)}</span></div>
    </div>
  );
}
